import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MicVAD, utils as vadUtils } from '@ricky0123/vad-web';
import Modal from './Modal';
import { FuturisticSoundwave } from './FuturisticSoundwave';
import { getVoiceCoachWsToken } from '../api/dataService';
import config from '../config/config';
import { useCoachQuota } from '../hooks/useCoachQuota';
import logger from '../utils/logger';
import {
  buildLiveCoachClientHello,
  buildLiveCoachFallbackAudioSrc,
  getLiveCoachStartupTimeoutMessage,
  isLiveCoachCompletionAction,
  isLiveCoachQuotaReached,
  isLiveCoachStartupReady,
  upsertLiveCoachAssistantTranscript,
} from '../utils/liveCoachSession';
import { useLocale } from '../context/LocaleContext';

const PCM_BYTES_PER_SAMPLE = 2;

function normalizePublicUrl() {
  const raw = process.env.PUBLIC_URL;
  if (!raw) return '';
  return raw === '/' ? '' : raw.replace(/\/$/, '');
}

function toWsBaseUrl(httpBaseUrl) {
  if (!httpBaseUrl) return '';
  if (httpBaseUrl.startsWith('https://')) return httpBaseUrl.replace('https://', 'wss://');
  if (httpBaseUrl.startsWith('http://')) return httpBaseUrl.replace('http://', 'ws://');
  return httpBaseUrl;
}

function joinUrl(base, path) {
  const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
}

function decodePcm16leToFloat32(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength < PCM_BYTES_PER_SAMPLE) {
    return new Float32Array(0);
  }
  const alignedByteLength = arrayBuffer.byteLength - (arrayBuffer.byteLength % PCM_BYTES_PER_SAMPLE);
  if (alignedByteLength <= 0) {
    return new Float32Array(0);
  }
  const alignedBuffer = alignedByteLength === arrayBuffer.byteLength
    ? arrayBuffer
    : arrayBuffer.slice(0, alignedByteLength);
  const pcm = new Int16Array(alignedBuffer);
  const float = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) {
    float[i] = pcm[i] / 32768;
  }
  return float;
}

function mergeUint8Chunks(chunks, totalBytes) {
  const merged = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    const view = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    merged.set(view, offset);
    offset += view.byteLength;
  }
  return merged;
}

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const LIVE_COACH_HANDSHAKE_TIMEOUT_MS = 8000;
const LIVE_COACH_READY_TIMEOUT_MS = 20000;
const LIVE_COACH_WS_DEBUG = process.env.REACT_APP_LIVE_COACH_WS_DEBUG === 'true';
const LIVE_COACH_MIN_CLIENT_UTTERANCE_MS = 120;
const LIVE_COACH_MIN_SPEECH_MS = 180;

function liveCoachWsDebug(event, details = {}) {
  if (!LIVE_COACH_WS_DEBUG) return;
  logger.log(`[CoachSessionModal WS Debug] ${event}`, details);
}

function getWsReadyStateLabel(readyState) {
  switch (readyState) {
    case 0:
      return 'CONNECTING';
    case 1:
      return 'OPEN';
    case 2:
      return 'CLOSING';
    case 3:
      return 'CLOSED';
    default:
      return String(readyState);
  }
}

const CoachSessionModal = ({ quest, onClose, onSessionComplete }) => {
  const [transcript, setTranscript] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [phase, setPhase] = useState('starting');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [liveReady, setLiveReady] = useState(false);
  const { t } = useLocale();

  const {
    coachLimitReached,
    usageSeconds,
    limitMinutes,
    refreshCoachUsage,
    setCoachUsage,
    markLimitReached,
    error: quotaError,
  } = useCoachQuota();

  const wsRef = useRef(null);
  const vadRef = useRef(null);
  const audioContextRef = useRef(null);
  const scheduledAtRef = useRef(0);
  const scheduledNodesRef = useRef([]);
  const bufferedPcmChunksRef = useRef([]);
  const bufferedPcmBytesRef = useRef(0);
  const currentGenerationSeqRef = useRef(null);
  const currentAssistantIdRef = useRef(null);
  const audioElementRef = useRef(null);
  const speakingRef = useRef(false);
  const serverPhaseRef = useRef('idle');
  const sessionActiveRef = useRef(false);
  const maxUtteranceTimerRef = useRef(null);
  const assistantTextByGenerationRef = useRef(new Map());
  const stopSessionRef = useRef(null);
  const downlinkSampleRateRef = useRef(24000);
  const liveReadyRef = useRef(false);
  const openerServerDoneRef = useRef(false);
  const openerPlaybackDoneRef = useRef(false);
  const vadStartingRef = useRef(false);
  const clientHelloAckRef = useRef(false);
  const handshakeTimerRef = useRef(null);
  const readyTimerRef = useRef(null);
  const conversationContextIdRef = useRef(`live-${Date.now()}-${Math.floor(Math.random() * 1e6)}`);
  const limitMinutesRef = useRef(limitMinutes);
  const setCoachUsageRef = useRef(setCoachUsage);
  const markLimitReachedRef = useRef(markLimitReached);
  const maybeStartListeningRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onSessionCompleteRef = useRef(onSessionComplete);
  const refreshCoachUsageRef = useRef(refreshCoachUsage);
  const ensureAudioContextRef = useRef(null);
  const connectWsRef = useRef(null);
  const completionClosingRef = useRef(false);
  const lastBargeInAtRef = useRef(0);
  const uplinkSampleRate = 16000;

  const deviceInfo = useMemo(() => ({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    vendor: navigator.vendor,
  }), []);

  const vadAssetConfig = useMemo(() => {
    const base = normalizePublicUrl();
    return {
      baseAssetPath: `${base}/voice/vad/`,
      onnxWASMBasePath: `${base}/voice/ort/`,
    };
  }, []);

  const appendTranscript = useCallback((entry) => {
    setTranscript((prev) => [...prev, entry]);
  }, []);

  const upsertAssistantTranscript = useCallback((generationSeq, text) => {
    if (!generationSeq || !text) return;
    const existingId = currentAssistantIdRef.current || makeId('agent');
    if (!currentAssistantIdRef.current) {
      currentAssistantIdRef.current = existingId;
    }
    setTranscript((prev) => {
      return upsertLiveCoachAssistantTranscript(prev, {
        id: existingId,
        text,
        timestamp: Date.now(),
      });
    });
  }, []);

  const ensureAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      scheduledAtRef.current = audioContextRef.current.currentTime;
      return;
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
  }, []);

  const stopHttpAudio = useCallback(() => {
    if (!audioElementRef.current) return;
    try {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
    } catch {
      // ignore
    }
    audioElementRef.current = null;
  }, []);

  const stopPlayback = useCallback(() => {
    stopHttpAudio();
    for (const node of scheduledNodesRef.current) {
      try {
        node.stop(0);
      } catch {
        // ignore
      }
      try {
        node.disconnect();
      } catch {
        // ignore
      }
    }
    scheduledNodesRef.current = [];
    bufferedPcmChunksRef.current = [];
    bufferedPcmBytesRef.current = 0;
    speakingRef.current = false;
    setIsSpeaking(false);

    const ctx = audioContextRef.current;
    scheduledAtRef.current = ctx ? ctx.currentTime : 0;
  }, [stopHttpAudio]);

  const sendWsJson = useCallback((payload) => {
    const ws = wsRef.current;
    liveCoachWsDebug('send_attempt', {
      messageType: payload?.type || null,
      questId: quest?._id,
      conversationContextId: conversationContextIdRef.current,
      readyState: ws?.readyState ?? null,
      readyStateLabel: ws ? getWsReadyStateLabel(ws.readyState) : 'missing_ws',
      payloadBytes: payload ? JSON.stringify(payload).length : 0,
      source: 'sendWsJson',
    });
    if (!ws || ws.readyState !== 1) {
      liveCoachWsDebug('send_skipped', {
        messageType: payload?.type || null,
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws?.readyState ?? null,
        readyStateLabel: ws ? getWsReadyStateLabel(ws.readyState) : 'missing_ws',
        source: 'sendWsJson',
      });
      return;
    }
    try {
      ws.send(JSON.stringify(payload));
      liveCoachWsDebug('send_success', {
        messageType: payload?.type || null,
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
        source: 'sendWsJson',
      });
    } catch {
      liveCoachWsDebug('send_failure', {
        messageType: payload?.type || null,
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
        source: 'sendWsJson',
      });
    }
  }, [quest?._id]);

  const clearStartupTimers = useCallback(() => {
    if (handshakeTimerRef.current) {
      clearTimeout(handshakeTimerRef.current);
      handshakeTimerRef.current = null;
    }
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
  }, []);

  const scheduleStartupTimers = useCallback((hello) => {
    clearStartupTimers();
    handshakeTimerRef.current = setTimeout(() => {
      if (!sessionActiveRef.current || clientHelloAckRef.current) return;
      logger.error('[CoachSessionModal] Live Coach websocket did not acknowledge client_hello', {
        questId: hello?.questId,
        conversationContextId: hello?.conversationContextId,
      });
      liveCoachWsDebug('handshake_timeout', {
        reason: 'missing_client_hello_ack',
        questId: hello?.questId,
        conversationContextId: hello?.conversationContextId,
        clientHelloAck: clientHelloAckRef.current,
        liveReady: liveReadyRef.current,
      });
      setInternalError(getLiveCoachStartupTimeoutMessage({ clientHelloAck: false, t }));
    }, LIVE_COACH_HANDSHAKE_TIMEOUT_MS);

    readyTimerRef.current = setTimeout(() => {
      if (!sessionActiveRef.current || liveReadyRef.current || !clientHelloAckRef.current) return;
      logger.error('[CoachSessionModal] Live Coach workout readiness timed out', {
        questId: hello?.questId,
        conversationContextId: hello?.conversationContextId,
        clientHelloAck: clientHelloAckRef.current,
        openerServerDone: openerServerDoneRef.current,
      });
      liveCoachWsDebug('handshake_timeout', {
        reason: 'missing_live_coach_ready',
        questId: hello?.questId,
        conversationContextId: hello?.conversationContextId,
        clientHelloAck: clientHelloAckRef.current,
        openerServerDone: openerServerDoneRef.current,
        openerPlaybackDone: openerPlaybackDoneRef.current,
        liveReady: liveReadyRef.current,
      });
      setInternalError(getLiveCoachStartupTimeoutMessage({ clientHelloAck: true, t }));
    }, LIVE_COACH_READY_TIMEOUT_MS);
  }, [clearStartupTimers]);

  const flushBufferedAudio = useCallback(async () => {
    const ctx = audioContextRef.current;
    const generationSeq = currentGenerationSeqRef.current;
    if (!ctx || !generationSeq) return;

    const totalBytes = bufferedPcmBytesRef.current;
    if (!totalBytes) return;

    const merged = mergeUint8Chunks(bufferedPcmChunksRef.current, totalBytes);
    bufferedPcmChunksRef.current = [];
    bufferedPcmBytesRef.current = 0;

    const sampleRate = downlinkSampleRateRef.current || 24000;
    const pcmBuffer = merged.buffer.slice(merged.byteOffset, merged.byteOffset + merged.byteLength);
    const float = decodePcm16leToFloat32(pcmBuffer);
    if (float.length === 0) return;
    const audioBuffer = ctx.createBuffer(1, float.length, sampleRate);
    audioBuffer.copyToChannel(float, 0);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now + 0.02, scheduledAtRef.current);
    scheduledAtRef.current = startAt + audioBuffer.duration;
    scheduledNodesRef.current.push(source);
    speakingRef.current = true;
    setIsSpeaking(true);
    source.start(startAt);

    source.onended = () => {
      scheduledNodesRef.current = scheduledNodesRef.current.filter((node) => node !== source);
      if (scheduledNodesRef.current.length === 0 && bufferedPcmBytesRef.current === 0) {
        speakingRef.current = false;
        setIsSpeaking(false);
      }
    };
  }, []);

  const enqueueAudioFrame = useCallback((generationSeq, audioArrayBuffer) => {
    if (!audioArrayBuffer || audioArrayBuffer.byteLength === 0) return;
    if (generationSeq !== currentGenerationSeqRef.current) return;

    bufferedPcmChunksRef.current.push(new Uint8Array(audioArrayBuffer));
    bufferedPcmBytesRef.current += audioArrayBuffer.byteLength;
    if (bufferedPcmBytesRef.current >= 2400) {
      void flushBufferedAudio();
    }
  }, [flushBufferedAudio]);

  const waitForPlaybackIdle = useCallback((timeoutMs = 30000) => {
    const startedAt = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        const idle =
          scheduledNodesRef.current.length === 0 &&
          bufferedPcmBytesRef.current === 0;
        if (idle) {
          resolve(true);
          return;
        }
        if (Date.now() - startedAt >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }, []);

  const playHttpAudio = useCallback(async (src, { waitForEnd = false } = {}) => {
    stopPlayback();
    if (!src) return false;
    try {
      const audio = new Audio();
      audio.src = src;
      audio.load();
      audioElementRef.current = audio;
      speakingRef.current = true;
      setIsSpeaking(true);
      const ended = new Promise((resolve) => {
        const finish = () => {
          audio.removeEventListener('ended', finish);
          audio.removeEventListener('error', finish);
          resolve();
        };
        audio.addEventListener('ended', finish);
        audio.addEventListener('error', finish);
      });
      audio.addEventListener('ended', () => {
        speakingRef.current = false;
        setIsSpeaking(false);
      });
      audio.addEventListener('error', () => {
        speakingRef.current = false;
        setIsSpeaking(false);
      });
      await audio.play();
      if (waitForEnd) {
        await ended;
      }
      return true;
    } catch {
      speakingRef.current = false;
      setIsSpeaking(false);
      return false;
    }
  }, [stopPlayback]);

  const handleSpeechStart = useCallback(() => {
    if (!sessionActiveRef.current) return;
    if (speakingRef.current) {
      stopPlayback();
      const now = Date.now();
      if (now - lastBargeInAtRef.current > 250) {
        lastBargeInAtRef.current = now;
        sendWsJson({ type: 'barge_in', reason: 'user_speaking_over_audio' });
      }
    }
  }, [sendWsJson, stopPlayback]);

  const handleSpeechRealStart = useCallback(async () => {
    if (!sessionActiveRef.current) return;
    const serverPhase = serverPhaseRef.current;
    if (serverPhase && serverPhase !== 'listening') {
      stopPlayback();
      const now = Date.now();
      if (now - lastBargeInAtRef.current > 250) {
        lastBargeInAtRef.current = now;
        sendWsJson({ type: 'barge_in', reason: 'user_speaking' });
      }
    }

    if (maxUtteranceTimerRef.current) {
      clearTimeout(maxUtteranceTimerRef.current);
    }
    maxUtteranceTimerRef.current = setTimeout(async () => {
      const vad = vadRef.current;
      if (!vad || !sessionActiveRef.current) return;
      try {
        await vad.pause();
        await vad.start();
      } catch {
        // ignore
      }
    }, 180000);
  }, [sendWsJson, stopPlayback]);

  const handleSpeechEnd = useCallback(async (audio) => {
    if (!sessionActiveRef.current || !audio) return;
    if (maxUtteranceTimerRef.current) {
      clearTimeout(maxUtteranceTimerRef.current);
      maxUtteranceTimerRef.current = null;
    }

    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) {
      setInternalError(t('liveCoach.connectionUnavailable', 'Live Coach connection unavailable. Close and try again.'));
      return;
    }

    const wavArrayBuffer = vadUtils.encodeWAV(audio, 1, uplinkSampleRate, 1, 16);
    const durationMs = Math.round((audio.length / uplinkSampleRate) * 1000);
    if (durationMs < LIVE_COACH_MIN_CLIENT_UTTERANCE_MS) return;

    const utteranceId = makeId('utt');
    sendWsJson({
      type: 'start_utterance',
      utteranceId,
      ttsMode: 'server_pcm',
    });
    try {
      ws.send(wavArrayBuffer);
    } catch {
      setInternalError(t('liveCoach.audioSendFailed', 'Live Coach audio send failed. Close and try again.'));
      return;
    }
    sendWsJson({ type: 'end_utterance', utteranceId });
  }, [sendWsJson, t]);

  const startVAD = useCallback(async () => {
    if (vadRef.current) return;
    const vad = await MicVAD.new({
      model: 'v5',
      startOnLoad: false,
      processorType: 'auto',
      redemptionMs: 700,
      preSpeechPadMs: 250,
      minSpeechMs: LIVE_COACH_MIN_SPEECH_MS,
      submitUserSpeechOnPause: true,
      baseAssetPath: vadAssetConfig.baseAssetPath,
      onnxWASMBasePath: vadAssetConfig.onnxWASMBasePath,
      onSpeechStart: handleSpeechStart,
      onSpeechRealStart: handleSpeechRealStart,
      onSpeechEnd: handleSpeechEnd,
    });
    vadRef.current = vad;
    await vad.start();
  }, [handleSpeechEnd, handleSpeechRealStart, handleSpeechStart, vadAssetConfig]);

  const maybeStartListening = useCallback(async () => {
    if (!sessionActiveRef.current || vadRef.current || vadStartingRef.current) return;
    if (!isLiveCoachStartupReady({
      liveReady: liveReadyRef.current,
      openerServerDone: openerServerDoneRef.current,
      openerPlaybackDone: openerPlaybackDoneRef.current,
    })) return;

    vadStartingRef.current = true;
    try {
      await startVAD();
      setPhase('listening');
    } catch (err) {
      logger.error('[CoachSessionModal] Failed to start Live Coach microphone:', err);
      setInternalError(t('liveCoach.microphoneStartFailed', 'Live Coach microphone failed to start. Close and try again.'));
    } finally {
      vadStartingRef.current = false;
    }
  }, [startVAD, t]);

  useEffect(() => {
    limitMinutesRef.current = limitMinutes;
    setCoachUsageRef.current = setCoachUsage;
    markLimitReachedRef.current = markLimitReached;
    maybeStartListeningRef.current = maybeStartListening;
    onCloseRef.current = onClose;
    onSessionCompleteRef.current = onSessionComplete;
    refreshCoachUsageRef.current = refreshCoachUsage;
    ensureAudioContextRef.current = ensureAudioContext;
  }, [
    ensureAudioContext,
    limitMinutes,
    markLimitReached,
    maybeStartListening,
    onClose,
    onSessionComplete,
    refreshCoachUsage,
    setCoachUsage,
  ]);

  const stopVAD = useCallback(async () => {
    if (maxUtteranceTimerRef.current) {
      clearTimeout(maxUtteranceTimerRef.current);
      maxUtteranceTimerRef.current = null;
    }
    const vad = vadRef.current;
    vadRef.current = null;
    if (vad) {
      try {
        await vad.destroy();
      } catch {
        // ignore
      }
    }
  }, []);

  const connectWs = useCallback((sessionToken) => {
    const wsBase = toWsBaseUrl(config.api.baseUrl);
    const wsPath = '/voice/ws';
    const wsUrl = joinUrl(wsBase, `${wsPath}?sessionToken=${encodeURIComponent(sessionToken)}`);
    liveCoachWsDebug('construct', {
      apiBaseUrl: config.api.baseUrl,
      wsBaseUrl: wsBase,
      wsPath,
      questId: quest?._id,
      conversationContextId: conversationContextIdRef.current,
      hasSessionToken: Boolean(sessionToken),
      sessionTokenLength: sessionToken ? sessionToken.length : 0,
    });
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    setConnectionStatus('connecting');
    let clientHelloSent = false;
    let pendingHello = null;

    const sendClientHello = (hello, source) => {
      if (!hello || clientHelloSent) return;
      try {
        const payload = JSON.stringify(hello);
        logger.log('[CoachSessionModal] sending client_hello', {
          mode: hello.mode,
          questId: hello.questId,
          conversationContextId: hello.conversationContextId,
          wsReadyState: ws.readyState,
          refMatch: wsRef.current === ws,
          source,
        });
        liveCoachWsDebug('send_attempt', {
          messageType: hello.type,
          mode: hello.mode,
          questId: hello.questId,
          conversationContextId: hello.conversationContextId,
          readyState: ws.readyState,
          readyStateLabel: getWsReadyStateLabel(ws.readyState),
          payloadBytes: payload.length,
          source,
        });
        ws.send(payload);
        clientHelloSent = true;
        liveCoachWsDebug('send_success', {
          messageType: hello.type,
          mode: hello.mode,
          questId: hello.questId,
          conversationContextId: hello.conversationContextId,
          readyState: ws.readyState,
          readyStateLabel: getWsReadyStateLabel(ws.readyState),
          payloadBytes: payload.length,
          source,
        });
      } catch (err) {
        logger.error('[CoachSessionModal] client_hello send failed', err);
        liveCoachWsDebug('send_failure', {
          messageType: hello.type,
          questId: hello.questId,
          conversationContextId: hello.conversationContextId,
          readyState: ws.readyState,
          readyStateLabel: getWsReadyStateLabel(ws.readyState),
          error: err?.message || String(err),
          source,
        });
      }
    };

    ws.onopen = () => {
      setConnectionStatus('connected');
      liveCoachWsDebug('open', {
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
        refMatch: wsRef.current === ws,
      });
      const hello = buildLiveCoachClientHello({
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        deviceInfo,
        ttsMode: 'server_pcm',
      });
      pendingHello = hello;
      clientHelloAckRef.current = false;
      scheduleStartupTimers(hello);
      liveCoachWsDebug('client_hello_deferred_until_server_hello', {
        mode: hello.mode,
        questId: hello.questId,
        conversationContextId: hello.conversationContextId,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
      });
    };

    ws.onmessage = async (event) => {
      if (typeof event.data === 'string') {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          liveCoachWsDebug('message_received', {
            dataType: 'text',
            parsed: false,
            byteLength: event.data.length,
            readyState: ws.readyState,
            readyStateLabel: getWsReadyStateLabel(ws.readyState),
          });
          return;
        }
        liveCoachWsDebug('message_received', {
          dataType: 'text',
          parsed: true,
          messageType: message?.type || null,
          connectionId: message?.connectionId || null,
          questId: message?.questId || null,
          generationSeq: message?.generationSeq || null,
          byteLength: event.data.length,
          readyState: ws.readyState,
          readyStateLabel: getWsReadyStateLabel(ws.readyState),
        });

        if (message?.type === 'server_hello') {
          liveCoachWsDebug('server_hello_received', {
            connectionId: message?.connectionId || null,
            idleTimeoutMs: message?.idleTimeoutMs || null,
            questId: pendingHello?.questId || null,
            conversationContextId: pendingHello?.conversationContextId || null,
            clientHelloSent,
            readyState: ws.readyState,
            readyStateLabel: getWsReadyStateLabel(ws.readyState),
          });
          sendClientHello(pendingHello, 'server_hello');
          return;
        }

        if (message?.type === 'status') {
          serverPhaseRef.current = message.phase;
          if (message.phase === 'listening' && !vadRef.current) {
            setPhase('starting');
          } else {
            setPhase(message.phase);
          }
          return;
        }

        if (message?.type === 'client_hello_ack') {
          clientHelloAckRef.current = true;
          if (handshakeTimerRef.current) {
            clearTimeout(handshakeTimerRef.current);
            handshakeTimerRef.current = null;
          }
          logger.log('[CoachSessionModal] client_hello_ack', {
            mode: message.mode,
            questId: message.questId,
            ttsMode: message.ttsMode,
          });
          if (message.mode !== 'live_coach') {
            setInternalError(t('liveCoach.wrongQuestContext', 'Live Coach did not receive the selected quest context. Close and try again.'));
          }
          return;
        }

        if (message?.type === 'live_coach_opener_start') {
          logger.log('[CoachSessionModal] live_coach_opener_start', {
            generationSeq: message.generationSeq,
          });
          openerServerDoneRef.current = false;
          openerPlaybackDoneRef.current = false;
          return;
        }

        if (message?.type === 'live_coach_opener_done') {
          logger.log('[CoachSessionModal] live_coach_opener_done', {
            generationSeq: message.generationSeq,
            audioSent: message.audioSent,
            provider: message.provider,
          });
          openerServerDoneRef.current = true;
          if (!message.audioSent) {
            openerPlaybackDoneRef.current = true;
          }
          void maybeStartListeningRef.current?.();
          return;
        }

        if (message?.type === 'live_coach_ready') {
          const ready = Boolean(message.workoutLoaded);
          if (readyTimerRef.current) {
            clearTimeout(readyTimerRef.current);
            readyTimerRef.current = null;
          }
          logger.log('[CoachSessionModal] live_coach_ready', {
            ready,
            questId: message.questId,
            totalSets: message.totalSets,
          });
          liveReadyRef.current = ready;
          setLiveReady(ready);
          if (ready) {
            void maybeStartListeningRef.current?.();
          }
          return;
        }

        if (message?.type === 'live_coach_state') {
          return;
        }

        if (message?.type === 'stt_final' && message.text) {
          appendTranscript({
            id: makeId('user'),
            author: 'user',
            text: message.text,
            timestamp: Date.now(),
          });
          return;
        }

        if (message?.type === 'assistant_start') {
          currentGenerationSeqRef.current = message.generationSeq;
          const id = makeId('agent');
          currentAssistantIdRef.current = id;
          assistantTextByGenerationRef.current.set(message.generationSeq, '');
          return;
        }

        if (message?.type === 'assistant_delta') {
          const generationSeq = message.generationSeq;
          const prev = assistantTextByGenerationRef.current.get(generationSeq) || '';
          const next = `${prev}${message.textDelta || ''}`;
          assistantTextByGenerationRef.current.set(generationSeq, next);
          upsertAssistantTranscript(generationSeq, next);
          return;
        }

        if (message?.type === 'assistant_final') {
          assistantTextByGenerationRef.current.set(message.generationSeq, message.text || '');
          upsertAssistantTranscript(message.generationSeq, message.text || '');
          currentAssistantIdRef.current = null;
          return;
        }

        if (message?.type === 'tts_start') {
          currentGenerationSeqRef.current = message.generationSeq;
          downlinkSampleRateRef.current = message?.audio?.sampleRateHz || 24000;
          if (message.source === 'live_coach_opener') {
            openerPlaybackDoneRef.current = false;
          }
          stopPlayback();
          return;
        }

        if (message?.type === 'tts_end') {
          const isOpener = message.source === 'live_coach_opener';
          await flushBufferedAudio();
          if (isOpener) {
            await waitForPlaybackIdle();
            openerPlaybackDoneRef.current = true;
            void maybeStartListeningRef.current?.();
          }
          return;
        }

        if (message?.type === 'tts_fallback') {
          const audioSrc = buildLiveCoachFallbackAudioSrc(message);
          if (audioSrc) {
            const isOpener = message.source === 'live_coach_opener';
            await playHttpAudio(audioSrc, { waitForEnd: isOpener });
            if (isOpener) {
              openerPlaybackDoneRef.current = true;
              void maybeStartListeningRef.current?.();
            }
          }
          return;
        }

        if (message?.type === 'generation_canceled') {
          stopPlayback();
          currentGenerationSeqRef.current = null;
          currentAssistantIdRef.current = null;
          return;
        }

        if (message?.type === 'usage') {
          setCoachUsageRef.current?.({
            usageSeconds: message.monthlySeconds ?? 0,
            limitMinutes: message.limitMinutes ?? limitMinutesRef.current ?? 0,
          });
          return;
        }

        if (isLiveCoachCompletionAction(message, quest?._id)) {
          if (completionClosingRef.current) return;
          completionClosingRef.current = true;
          logger.log('[CoachSessionModal] completeWorkout action received; closing Live Coach modal', {
            questId: quest?._id,
            conversationContextId: conversationContextIdRef.current,
          });
          await stopSessionRef.current?.();
          onSessionCompleteRef.current?.();
          onCloseRef.current?.();
          return;
        }

        if (message?.type === 'actions') {
          return;
        }

        if (message?.type === 'quota_exceeded' || message?.type === 'quota_reached') {
          clearStartupTimers();
          markLimitReachedRef.current?.();
          setInternalError(message.message || t('liveCoach.limitReachedBody', 'Monthly AI Coach limit reached.'));
          void stopSessionRef.current?.();
          return;
        }

        if (message?.type === 'idle_timeout') {
          setInternalError(t('liveCoach.inactiveTimeout', 'Session closed due to inactivity.'));
          void stopSessionRef.current?.();
          return;
        }

        if (message?.type === 'error') {
          clearStartupTimers();
          setInternalError(message.error || t('liveCoach.genericError', 'Live Coach error.'));
        }
        return;
      }

      const buffer = event.data;
      if (!(buffer instanceof ArrayBuffer)) return;
      if (buffer.byteLength <= 4) {
        liveCoachWsDebug('message_received', {
          dataType: 'binary',
          generationSeq: null,
          byteLength: buffer.byteLength,
          audioBytes: 0,
          readyState: ws.readyState,
          readyStateLabel: getWsReadyStateLabel(ws.readyState),
        });
        return;
      }

      const view = new DataView(buffer);
      const generationSeq = view.getUint32(0, true);
      liveCoachWsDebug('message_received', {
        dataType: 'binary',
        generationSeq,
        byteLength: buffer.byteLength,
        audioBytes: Math.max(0, buffer.byteLength - 4),
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
      });
      const audioBuffer = buffer.slice(4);
      enqueueAudioFrame(generationSeq, audioBuffer);
    };

    ws.onclose = (event) => {
      liveCoachWsDebug('close', {
        code: event?.code,
        reason: event?.reason || '',
        wasClean: Boolean(event?.wasClean),
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
      });
      clearStartupTimers();
      wsRef.current = null;
      setConnectionStatus('disconnected');
      if (sessionActiveRef.current) {
        setInternalError(t('liveCoach.connectionClosed', 'Live Coach connection closed.'));
      }
    };

    ws.onerror = (event) => {
      liveCoachWsDebug('error', {
        eventType: event?.type || 'error',
        questId: quest?._id,
        conversationContextId: conversationContextIdRef.current,
        readyState: ws.readyState,
        readyStateLabel: getWsReadyStateLabel(ws.readyState),
      });
      clearStartupTimers();
      setInternalError(t('liveCoach.connectionFailed', 'Live Coach connection failed.'));
    };
  }, [
    appendTranscript,
    clearStartupTimers,
    deviceInfo,
    enqueueAudioFrame,
    flushBufferedAudio,
    playHttpAudio,
    quest?._id,
    scheduleStartupTimers,
    sendWsJson,
    stopPlayback,
    t,
    upsertAssistantTranscript,
    waitForPlaybackIdle,
  ]);

  useEffect(() => {
    connectWsRef.current = connectWs;
  }, [connectWs]);

  const stopSession = useCallback(async () => {
    sessionActiveRef.current = false;
    liveReadyRef.current = false;
    openerServerDoneRef.current = false;
    openerPlaybackDoneRef.current = false;
    vadStartingRef.current = false;
    clientHelloAckRef.current = false;
    clearStartupTimers();
    stopPlayback();
    await stopVAD();
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }
    setIsSpeaking(false);
    setPhase('idle');
    setConnectionStatus('disconnected');
  }, [clearStartupTimers, stopPlayback, stopVAD]);

  useEffect(() => {
    stopSessionRef.current = stopSession;
  }, [stopSession]);

  useEffect(() => {
    let canceled = false;

    const startSession = async () => {
      try {
        setIsLoading(true);
        setInternalError(null);
        liveReadyRef.current = false;
        openerServerDoneRef.current = false;
        openerPlaybackDoneRef.current = false;
        vadStartingRef.current = false;
        clientHelloAckRef.current = false;
        completionClosingRef.current = false;
        lastBargeInAtRef.current = 0;
        clearStartupTimers();
        setLiveReady(false);
        setPhase('starting');

        const latestUsage = await refreshCoachUsageRef.current?.();
        const effectiveUsageSeconds = latestUsage?.usageSeconds ?? 0;
        const effectiveLimitMinutes = latestUsage?.limitMinutes ?? -1;
        if (isLiveCoachQuotaReached({
          usageSeconds: effectiveUsageSeconds,
          limitMinutes: effectiveLimitMinutes,
        })) {
          markLimitReachedRef.current?.();
          return;
        }

        await ensureAudioContextRef.current?.();
        const tokenResponse = await getVoiceCoachWsToken();
        const sessionToken = tokenResponse?.data?.sessionToken;
        if (!sessionToken) {
          throw new Error(t('liveCoach.sessionCreateFailed', 'Failed to create Live Coach session.'));
        }

        if (canceled) return;
        sessionActiveRef.current = true;
        connectWsRef.current?.(sessionToken);
        if (canceled) return;
      } catch (err) {
        logger.error('[CoachSessionModal] Failed to start custom Live Coach:', err);
        if (err?.response?.status === 429) {
          markLimitReachedRef.current?.();
        } else {
          setInternalError(err?.message || t('liveCoach.connectFailed', 'Failed to connect.'));
        }
      } finally {
        if (!canceled) {
          setIsLoading(false);
        }
      }
    };

    void startSession();

    return () => {
      canceled = true;
      void stopSessionRef.current?.();
    };
  }, [quest?._id, t]);

  useEffect(() => {
    const list = document.querySelector('[data-live-coach-transcript]');
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [transcript]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p>{t('liveCoach.loading', 'Connecting to AI Coach...')}</p>
        </div>
      );
    }

    if (coachLimitReached) {
      return (
        <div className="text-center text-yellow-400">
          <p className="font-bold">{t('liveCoach.limitReachedTitle', 'Monthly Limit Reached')}</p>
          <p>{t('liveCoach.limitReachedBody', 'You have used all your AI Coach minutes for this month. Please upgrade for more.')}</p>
        </div>
      );
    }

    const error = internalError;
    if (error) {
      return (
        <div className="text-center text-red-400">
          <p className="font-bold">{t('liveCoach.connectionErrorTitle', 'Connection Error')}</p>
          <p>{error.message || error}</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full items-center">
        <div>
          <FuturisticSoundwave speaking={isSpeaking || phase === 'listening'} variant="hero" width={600} height={160} />
        </div>
        {!coachLimitReached && quotaError && (
          <div className="mt-2 text-xs text-red-400">
            {t('liveCoach.usageCacheWarning', 'Unable to refresh usage. Using cached quota.')}
          </div>
        )}
        <div className="mt-1 text-xs text-gray-500">
          {connectionStatus === 'connected'
            ? liveReady ? t('liveCoach.ready', 'Live Coach ready') : t('liveCoach.preparing', 'Preparing workout...')
            : t('liveCoach.connecting', 'Connecting...')}
        </div>
        <div
          data-live-coach-transcript
          className="w-full flex-grow mt-4 overflow-y-auto p-4 bg-gray-800/50 rounded-lg"
        >
          {transcript.map((entry) => (
            <div
              key={entry.id}
              className={`mb-2 ${entry.author === 'user' ? 'text-right' : 'text-left'}`}
            >
              <span
                className={`inline-block p-2 rounded-lg ${
                  entry.author === 'user' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-200'
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleClose = () => {
    void stopSession();
    if (onSessionComplete) {
      onSessionComplete();
    }
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={handleClose} title={quest.name} size="lg">
      <div style={{ height: '60vh' }} className="flex flex-col">
        {renderContent()}
      </div>
    </Modal>
  );
};

export default CoachSessionModal;
